/**
 * MiniShield — face-only variant of BriefingCreatorCard.
 *
 * Used in the radial network's outer (long-tail) ring where rendering
 * 100+ full-size shields would tank performance. Strips down to:
 *   • Face image (DiceBear fallback)
 *   • Role-colored rim (subtle, ~1.5px)
 *   • Small "+N" badge on the +rest pill
 *
 * No text, no flip, no metrics. Just a recognizable person at 48px.
 * Performance budget: ~120 of these can render concurrently without
 * frame drops because each instance is a single <img> + 1 div.
 */
import React from 'react';

function dicebearFor(seed) {
  const s = encodeURIComponent((seed || 'x').replace(/[^a-zA-Z0-9]/g, ''));
  return `https://api.dicebear.com/7.x/lorelei/svg?seed=${s}&backgroundColor=0a0a0a,0f0f0f,121212&radius=12`;
}

const ROLE_RIM = {
  patient_zero: '#FF4D94',
  bridge:       '#A78BFA',
  amplifier:    '#00D4FF',
  long_tail:    '#4A4A4A',
};

export default function MiniShield({ creator, role = 'long_tail', size = 44, dim = false }) {
  if (!creator) return null;
  const seed = creator.handle || creator.name || 'x';
  const avatar = creator.avatar || dicebearFor(seed);
  const rim = ROLE_RIM[role] || ROLE_RIM.long_tail;

  return (
    <div
      data-testid={`mini-shield-${creator.handle}`}
      data-role={role}
      title={`${creator.name || creator.handle} · ${role.replace('_', ' ')}`}
      className="rounded-full overflow-hidden transition-all duration-300"
      style={{
        width:   size,
        height:  size,
        border:  `1.5px solid ${rim}${dim ? '40' : 'cc'}`,
        opacity: dim ? 0.35 : 1,
        boxShadow: dim ? 'none' : `0 0 6px ${rim}44`,
      }}
    >
      <img
        src={avatar}
        alt={creator.name || creator.handle}
        className="w-full h-full object-cover"
        style={{ objectPosition: 'center top' }}
        loading="lazy"
        onError={(e) => { e.target.src = dicebearFor(seed); }}
      />
    </div>
  );
}
